---
meta:
  - title: Como usar Análisis de expedientes - IUSPRO Docs
  - description: Aprende a utilizar la herramienta de análisis de expedientes.
---

# Como usar Análisis de expedientes

La herramienta de Análisis de Expedientes analiza casos legales completos y genera reportes detallados con información valiosa.

## Tiempo de procesamiento

Usualmente tarda entre 20 minutos a 1 hora, dependiendo de qué tan largo sea el expediente.

## Notificación

Una vez termine el análisis, recibirá un correo electrónico con un link que lo lleva directamente a los resultados.

## ¿Qué genera el análisis?

Cuando sube un expediente, la plataforma genera automáticamente:

### Información básica

- **Título del caso**
- **Tipo de proceso**
- **Quién contra quién**
- **Resumen del caso**

### Análisis detallado

- **Identificación del caso**
- **Antecedentes**
- **Cuestiones legales principales**
- **Argumentos de las partes**
- **Evidencia crucial**
- **Estado actual del caso**
- **Próximos pasos**

### Cronología completa

Una tabla organizada con todos los eventos desde:

- Inicio de labores (si aplica)
- Notificaciones
- Todos los eventos relevantes del expediente

Puede ver fácilmente toda la línea de tiempo en una tabla clara.

## Reportes especializados

El sistema genera varios reportes en profundidad:

### 1. Reporte de Normativa

Busca toda la normativa relacionada con el expediente e identifica:

- **Requisitos formales** que no están presentes
- **Deficiencias procesales**
- **Estructura de argumentos** incompletos o mal fundamentados
- **Deficiencias sustantivas**

### 2. Análisis de Prescripción

Si es necesario, hace un análisis completo de prescripción que puede ser de gran ayuda.

### 3. Reportes de Jurisprudencia

Estos son especialmente valiosos. El sistema:

- Busca casos similares en Nexus
- Usa varios criterios para encontrar los más parecidos:
  - Pretensiones similares
  - Argumentos parecidos
  - Tipo de partes (indígena, corporación, etc.)

Para cada caso similar, muestra:

- **Similitudes claves** con su caso
- **Link a la resolución** en Nexus (puede verificar la fuente original)
- **Argumentos más efectivos** que funcionaron en las salas de corte
- **Diferencias críticas** únicas a su caso
- **Patrones** identificados
- **Recomendaciones estratégicas**

### Probabilidad de éxito

Calcula una probabilidad de éxito basada en:

- Similitudes con casos exitosos
- Riesgos principales identificados
- Recomendaciones específicas

### Estrategias legales

Una sección dedicada a:

- **Estrategias ganadoras**: Cómo usarlas en su caso
- **Estrategias perdedoras**: Qué evitar

## Estructura de los reportes

Cada reporte tiene dos partes:

1. **Resumen** (parte superior): Vista rápida del análisis
2. **Reporte completo** (parte inferior): Análisis detallado con toda la información

## Verificación de fuentes

Todas las citas a resoluciones y leyes incluyen links. Puede hacer clic en cualquier link para ir a Nexus y verificar la información original.

Esto es importante porque las IA pueden tener alucinaciones. Aunque el sistema está muy enfocado en Costa Rica y el riesgo es mucho menor, siempre existe. Por eso siempre se incluyen los links para que pueda verificar.

## Descargar reportes

Todos los reportes se pueden descargar en formato PDF. Busque el botón "Descargar PDF" en la parte superior.

## Valor del análisis

El análisis puede encontrar información que sería muy difícil encontrar manualmente porque:

- Analiza desde cientos hasta miles de jurisprudencias
- Identifica patrones que serían difíciles de ver
- Ahorra horas o días de investigación

Imagínese pedirle a un abogado que haga este nivel de análisis manualmente. Es muy valioso tener esta información de forma automática.

## Chat con el Asistente dentro del expediente

Una vez subido el expediente, tiene acceso a un chat con Auri que ya conoce todo el contexto del caso.

**Ventaja**: No tiene que explicarle cuál es su caso cada vez que pregunta algo. Él ya tiene el expediente completo.

Cualquier pregunta que haga será respondida con el contexto de ese expediente específico, y seguirá teniendo acceso a toda la jurisprudencia y normativa.

## Redactor dentro del expediente

También puede usar el Redactor desde dentro del expediente.

**Ventaja**: Cualquier documento que genere tendrá automáticamente el contexto del caso.

Por ejemplo, puede simplemente decir:

```
"Hazme un recurso así, así, así..."
```

Y el Redactor ya sabrá de qué caso está hablando, qué argumentos son relevantes, y creará el documento completo. Lo que tenga que modificarle es casi nada.

Esto ahorra horas de trabajo solo en crear recursos u otros documentos.
